import { logger } from "../services/logger.ts";
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Market, UserProfile } from "../types.ts";
import { SEO, getMarketStructuredData, getBreadcrumbStructuredData } from "../components/SEO.tsx";
import { 
  ArrowLeft, 
  Users, 
  Clock, 
  Award, 
  Percent, 
  AlertCircle,
  Share2,
  MessageSquare,
  Star,
  Trophy,
  Edit3,
  Lock,
  Clipboard,
  XCircle,
  BarChart,
  ChevronDown, // For expand
  ChevronUp    // For collapse
} from 'lucide-react';
import { MarketEngine } from "../lib/marketEngine.ts";
import { useAuth } from '../lib/auth.tsx';
import { supabase, checkSupabaseConnection } from '../lib/db.ts';
import { RealtimeChannel } from '@supabase/supabase-js';
import { GroupedBetHistory } from '../components/GroupedBetHistory.tsx';
import { PlaceBetForm } from '../components/PlaceBetForm.tsx';
import { FirstTradeGateModal } from '../components/FirstTradeGateModal.tsx';
import {
  SharePricedTradingIntroModal,
  dismissSharePricedIntroPersist,
  hasDismissedSharePricedIntro,
} from '../components/SharePricedTradingIntroModal.tsx';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { MarketDataFromDb } from '../lib/marketEngine.ts';
import { toast } from 'react-hot-toast';
import { MarketEditor as AdminMarketEditor } from '../components/admin/MarketEditor.tsx';
import { MarketEditor as CreatorMarketEditor } from '../components/creator/MarketEditor.tsx';
import { MarketResolver } from '../components/admin/MarketResolver.tsx';
import { MarketCanceller } from '../components/creator/MarketCanceller.tsx'; // New path
import { formatDateWithTimezone } from '../utils/dateUtils.ts';
import { formatSharePriceZar } from '../lib/displayFormat.ts';
import { MarketOptionsGraph } from '../components/MarketOptionsGraph.tsx';
import { useResolutionCountdown } from '../hooks/useResolutionCountdown.ts';
import {
  aggregateUserSharesByOption,
  computeAvgBuyPercentByOptionForLmsr,
} from '../lib/marketDetailHoldings.ts';

interface MarketDetailProps {
  isAdmin?: boolean;
}

interface MarketComment {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  email: string;
  user_metadata: {
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

const marketEngine = new MarketEngine();

// Helper function to check if user is admin
function checkIsAdmin(user: unknown): boolean {
  if (!user) return false;
  const userObj = user as { app_metadata?: { role?: string } };
  return userObj?.app_metadata?.role === 'admin';
}

// Debug helper component
const RenderDebug: React.FC<{ market: Market }> = ({ market }) => {
  React.useEffect(() => {
    logger.info('MarketDetail passing market to PlaceBetForm:', {
      id: market.id,
      is_private: market.is_private,
      fixed_fee: market.fixed_fee,
      fixed_fee_type: typeof market.fixed_fee
    });
  }, [market]);
  
  return null; // Renders nothing, just logs
};

export function MarketDetail({ isAdmin = false }: MarketDetailProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const [market, setMarket] = useState<Market | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<MarketComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [lmsrTradeMode, setLmsrTradeMode] = useState<'buy' | 'sell'>('buy');
  const [betHistoryRefresh, setBetHistoryRefresh] = useState(0);
  const [showFirstTradeGate, setShowFirstTradeGate] = useState(false);
  const [showSharePricedIntro, setShowSharePricedIntro] = useState(false);
  const { user } = useAuth();
  const userIsAdmin = user ? checkIsAdmin(user) : false;
  const [activeTab, setActiveTab] = useState('outcomes');
  const [isWatchlisted, setIsWatchlisted] = useState<boolean>(false);
  const [isUpdatingWatchlist, setIsUpdatingWatchlist] = useState<boolean>(false);
  const placeBetFormRef = useRef<HTMLDivElement>(null);
  /** One centered scroll per navigation when opening via `?trade=sell&option=` (e.g. portfolio Sell). */
  const portfolioSellScrollDoneRef = useRef(false);

  const scrollToPlaceBetForm = useCallback(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && placeBetFormRef.current) {
      placeBetFormRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  // Per-outcome share totals for the current user (used to pick "Trade Again" vs "Buy" on outcome cards).
  const [userSharesByOption, setUserSharesByOption] = useState<Record<number, number>>({});
  /** LMSR: weighted fair_probability at buys (% for price chart avg-buy reference lines). */
  const [avgBuyByOptionPercent, setAvgBuyByOptionPercent] = useState<Record<number, number>>({});
  const [marketActivity, setMarketActivity] = useState<{
    uniqueTraders: number;
    recentVolume: number;
    priceChange24h: number;
  }>({
    uniqueTraders: 0,
    recentVolume: 0,
    priceChange24h: 0
  });
  const [isMarketCreator, setIsMarketCreator] = useState<boolean>(false);
  const [realtimeStatus, setRealtimeStatus] = useState<string>('Initializing');
  const [totalPoolValue, setTotalPoolValue] = useState<number>(0);
  const [totalPoolUpdated, setTotalPoolUpdated] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('Never');
  const [isRejectionExpanded, setIsRejectionExpanded] = useState<boolean>(false); // New state for expansion
  const [isCancellationExpanded, setIsCancellationExpanded] = useState<boolean>(false); // New state for cancellation expansion
  const [optionHistory, setOptionHistory] = useState<Array<{
    timestamp: string;
    [key: string]: string | number;
  }>>([]);
  const [volumeHistory, setVolumeHistory] = useState<Array<{
    timestamp: string;
    [key: string]: string | number;
  }>>([]);
  const [chartMode, setChartMode] = useState<'price' | 'volume'>('price');
  const [selectedTimeRange, setSelectedTimeRange] = useState<'day' | 'week' | 'month' | 'all'>('all');

  const marketEngineTypeRef = useRef(market?.engine_type);
  useEffect(() => {
    marketEngineTypeRef.current = market?.engine_type;
  }, [market?.engine_type]);

  const { formatCurrency } = useSettings();

  const resolutionCountdown = useResolutionCountdown(market?.resolution_time ?? null, {
    enabled: market?.status === 'active',
  });

  // Option history: DB snapshots + live probabilities.
  // - Refetches after local trades (betHistoryRefresh)
  // - Subscribes to realtime inserts/updates on market_option_history and
  //   market_options for this market so the chart reflects other users'
  //   trades without a manual refresh.
  useEffect(() => {
    if (!id || !market) return;
    let cancelled = false;
    let scheduled = false;

    const fetchOptionHistory = async () => {
      const [history, vol] = await Promise.all([
        marketEngine.getMarketOptionHistory(id, selectedTimeRange),
        marketEngine.getMarketVolumeHistory(id, selectedTimeRange),
      ]);
      if (cancelled) return;
      setOptionHistory(marketEngine.mergeOptionHistoryWithLiveMarket(history, market));
      setVolumeHistory(marketEngine.normalizeVolumeHistoryForChart(vol, market));
    };

    // Coalesce bursty realtime events (e.g. a big trade inserts a history row
    // AND updates option outstanding_shares simultaneously).
    const refreshSoon = () => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => {
        scheduled = false;
        void fetchOptionHistory();
      }, 300);
    };

    void fetchOptionHistory();

    const channel = supabase
      .channel(`price-history-${id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'market_option_history', filter: `market_id=eq.${id}` },
        refreshSoon,
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'market_options', filter: `market_id=eq.${id}` },
        refreshSoon,
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [id, market, selectedTimeRange, betHistoryRefresh]);

  // ?trade=sell&option=<market_option_id> — open sell tab and select outcome (e.g. from portfolio).
  useEffect(() => {
    if (!market || market.engine_type !== 'lmsr') {
      setLmsrTradeMode('buy');
      return;
    }
    const trade = searchParams.get('trade');
    const opt = searchParams.get('option');
    setLmsrTradeMode(trade === 'sell' ? 'sell' : 'buy');
    if (trade === 'sell' && opt != null && opt !== '') {
      const n = parseInt(opt, 10);
      if (!Number.isNaN(n)) {
        setSelectedOptionId(n);
      }
    }
  }, [market?.id, market?.engine_type, searchParams]);

  useEffect(() => {
    portfolioSellScrollDoneRef.current = false;
  }, [id, searchParamsKey]);

  useEffect(() => {
    const trade = searchParams.get('trade');
    const opt = searchParams.get('option');
    if (trade !== 'sell' || opt == null || opt === '') return;
    const optionIdFromUrl = parseInt(opt, 10);
    if (Number.isNaN(optionIdFromUrl)) return;
    if (!user || !userProfile || !market || selectedOptionId !== optionIdFromUrl) return;
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) return;
    if (portfolioSellScrollDoneRef.current) return;
    portfolioSellScrollDoneRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToPlaceBetForm();
      });
    });
  }, [user, userProfile, market, selectedOptionId, searchParams, scrollToPlaceBetForm]);

  useEffect(() => {
    if (!market || market.engine_type !== 'lmsr') {
      setShowSharePricedIntro(false);
      return;
    }
    if (hasDismissedSharePricedIntro()) {
      setShowSharePricedIntro(false);
      return;
    }
    setShowSharePricedIntro(true);
  }, [market?.id, market?.engine_type]);

  // Diagnostic function for real-time connection status
  useEffect(() => {
    logger.info('Checking Supabase real-time connection status');
    const checkRealtimeStatus = () => {
      try {
        // Use the public API to check connection state
        const channels = supabase.getChannels();
        const status = channels.length > 0 
          ? 'CONNECTED (Channels: ' + channels.length + ')'
          : 'NO CHANNELS';
        
        logger.info('Real-time connection status:', { status });
        setRealtimeStatus(status);
        
        // If no channels, ensure we have a connection
        if (channels.length === 0) {
          logger.info('No active channels, reconnecting...');
          // We'll reconnect when setting up subscriptions
        }
      } catch (err) {
        logger.error('Error checking real-time status:', err);
        setRealtimeStatus('Error: ' + (err instanceof Error ? err.message : String(err)));
      }
    };
    
    // Check initially
    checkRealtimeStatus();
    
    // Then check periodically
    const intervalId = setInterval(checkRealtimeStatus, 10000);
    
    return () => clearInterval(intervalId);
  }, []);

  // For wallet balance updates (used in event handlers)
  const updateBalance = useCallback((newBalance: number) => {
    setUserProfile(prev => prev ? {...prev, wallet_balance: newBalance} : null);
  }, []);

  // Check if user has already placed bets for this market
  const checkExistingBets = useCallback(
    async (marketId: string, userId: string, engineType?: Market['engine_type']) => {
      try {
        const { data, error } = await supabase
          .from('user_bets')
          .select('id, status, market_option_id, shares_owned, amount, fair_price_at_trade')
          .eq('market_id', marketId)
          .eq('user_id', userId)
          .not('status', 'eq', 'cancelled'); // Exclude cancelled bets

        if (error) {
          logger.error('Error checking for existing bets:', error);
          setAvgBuyByOptionPercent({});
          return;
        }

        const rows = data ?? [];

        setUserSharesByOption(
          aggregateUserSharesByOption(
            rows as Array<{ market_option_id?: number | null; shares_owned?: number | null; amount?: number | null }>,
          ),
        );

        if (engineType !== 'lmsr') {
          setAvgBuyByOptionPercent({});
          return;
        }

        setAvgBuyByOptionPercent(
          computeAvgBuyPercentByOptionForLmsr(
            rows as Array<{
              market_option_id?: number | null;
              shares_owned?: number | null;
              fair_price_at_trade?: number | null;
            }>,
          ),
        );
      } catch (err) {
        logger.error('Exception checking for existing bets:', err);
        setAvgBuyByOptionPercent({});
      }
    },
    [],
  );

  // Check Supabase connection
  useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await checkSupabaseConnection();
      setConnectionError(!isConnected);
    };
    checkConnection();
  }, []);

  // Set the appropriate default tab when component mounts or when user auth state changes
  useEffect(() => {
    // Always default to outcomes tab
    setActiveTab('outcomes');
  }, [user]);

  // Function to fetch unique traders from user_bets table
  const fetchUniqueTraders = useCallback(async (marketId: string): Promise<number> => {
    try {
      // Use cached market data if available
      if (market && typeof market.unique_traders === 'number') {
        return market.unique_traders;
      }
      
      const { data, error } = await supabase
        .from('user_bets')
        .select('user_id')
        .eq('market_id', marketId);

      if (error) throw error;
      
      const uniqueTraders = new Set(data?.map(bet => bet.user_id)).size;
      return uniqueTraders;
    } catch (err) {
      logger.error('Error fetching unique traders:', err);
      return 0;
    }
  }, [supabase]);

  // Define fetchMarket using useCallback
  const fetchMarket = useCallback(async () => {
    if (!id || connectionError) return;
    setLoading(true);
    try {
      const marketData = await marketEngine.getMarket(id);
      if (!marketData) throw new Error('Market not found after fetch.');

      setMarket(marketData);
      setError(null);

      // Log active_bets_count when received
      if (marketData.active_bets_count !== undefined) {
        logger.info('Received active_bets_count:', marketData.active_bets_count);
      } else {
        logger.info('No active_bets_count data received');
      }

      // Initialize totalPoolValue from the market data
      const initialTotalPool = marketData.totalPool || 0;
      logger.info('Initializing totalPoolValue from market data:', initialTotalPool);
      setTotalPoolValue(initialTotalPool);
      
      const uniqueTraders = await fetchUniqueTraders(id);
      // Ensure price_change_24h is a number before setting state, provide default
      const priceChangeRaw = marketData.price_change_24h;
      const priceChangeParsed = typeof priceChangeRaw === 'string' ? parseFloat(priceChangeRaw) : priceChangeRaw;
      const priceChange = typeof priceChangeParsed === 'number' && !isNaN(priceChangeParsed) ? priceChangeParsed : 0;

      setMarketActivity(prev => ({
        ...prev,
        uniqueTraders: uniqueTraders ?? 0, // Ensure uniqueTraders is number
        priceChange24h: priceChange
      }));
      
      // Check if user has placed bets
      if (user && user.id) {
        await checkExistingBets(id, user.id, marketData.engine_type);
      }

    } catch (error) {
      logger.error('Error fetching market:', error);
      setError('Failed to load market details');
      setMarket(null); // Clear market data on error
    } finally {
      setLoading(false); // Unset loading state
    }
  }, [id, connectionError, user, checkExistingBets, fetchUniqueTraders, marketEngine]);

  // Initial market fetch when component mounts or dependencies change
  useEffect(() => {
    fetchMarket(); // Call the memoized fetch function
    
    // Set up a listener for bet history updates to refresh market data
    const handleBetHistoryUpdated = () => {
      logger.info('Bet history updated event received, but skipping refresh to prevent page reload');
      // Removed: fetchMarket(); // Don't refresh the entire market when bet history updates
    };
    
    window.addEventListener('betHistoryUpdated', handleBetHistoryUpdated);
    
    return () => {
      window.removeEventListener('betHistoryUpdated', handleBetHistoryUpdated);
    };
  }, [fetchMarket]); // Depend only on the stable fetchMarket function

  // Re-check for existing bets when betHistoryRefresh changes
  useEffect(() => {
    if (id && user && user.id) {
      void checkExistingBets(id, user.id, market?.engine_type);
    }
  }, [betHistoryRefresh, id, user, market?.engine_type, checkExistingBets]);

  // Fetch wallet balance updated
  useEffect(() => {
    if (!user || connectionError) return;

    const fetchUserProfile = async () => {
      try {
        // Fetch user wallet
        const { data: walletData, error: walletError } = await supabase
          .from('user_wallets')
          .select('balance')
          .eq('user_id', user.id)
          .single();

        if (walletError && walletError.code !== 'PGRST116') {
          throw walletError;
        }

        let walletBalance = 0;
        if (!walletData) {
          const { data: newWallet, error: createError } = await supabase
            .from('user_wallets')
            .insert({ user_id: user.id, balance: 0 })
            .select('balance')
            .single();

          if (createError) throw createError;
          if (newWallet && typeof newWallet === 'object' && 'balance' in newWallet) {
            walletBalance = newWallet.balance;
          }
        } else if (walletData && typeof walletData === 'object' && 'balance' in walletData) {
          walletBalance = walletData.balance;
        }

        updateBalance(walletBalance);

        // Create a simple user profile object
        setUserProfile({
          id: user.id,
          email: user.email || '',
          firstName: user.user_metadata?.first_name || '',
          lastName: user.user_metadata?.last_name || '',
          phone: user.user_metadata?.phone || '',
          avatar_url: user.user_metadata?.avatar_url,
          role: userIsAdmin ? 'admin' : 'user',
          created_at: '',
          updated_at: '',
          wallet_balance: walletBalance,
          total_trades: 0,
          win_rate: 0,
          positions: []
        });

        // Check if user has already placed a bet for this market
        if (id) {
          await checkExistingBets(id, user.id, market?.engine_type);
        }
      } catch (err) {
        logger.error('Error fetching user profile:', err);
      }
    };

    fetchUserProfile();

    // Wallet subscription
    const subscription = supabase
      .channel(`wallet-changes-${user.id}`) // Add user ID to make the channel name unique
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_wallets',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'balance' in payload.new) {
            const newBalance = payload.new.balance;
            logger.info('MarketDetail: Wallet subscription update received:', newBalance);
            updateBalance(newBalance);
            setUserProfile(prev => prev ? {...prev, wallet_balance: newBalance} : null);
          }
        }
      )
      .subscribe((status) => {
        logger.info(`MarketDetail: Wallet subscription status: ${status}`);
      });

    // Listen for custom wallet balance update events
    const handleWalletUpdate = (event: CustomEvent<{ balance: number }>) => {
      logger.info('Custom wallet update event received in MarketDetail:', event.detail.balance);
      updateBalance(event.detail.balance);
      setUserProfile(prev => prev ? {...prev, wallet_balance: event.detail.balance} : null);
    };

    window.addEventListener('walletBalanceUpdated', handleWalletUpdate as EventListener);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('walletBalanceUpdated', handleWalletUpdate as EventListener);
    };
  }, [user, connectionError, userIsAdmin, id, market?.engine_type, checkExistingBets]);

  // Function to fetch comments
  const fetchComments = useCallback(async () => {
    try {
      if (!id || connectionError) return;

      // First get the comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('market_comments')
        .select('*')
        .eq('market_id', id)
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;

      // Then get the user data for each comment
      const commentsWithUsers = await Promise.all(
        (commentsData || []).map(async (comment) => {
          try {
            const { data: userData, error: userError } = await supabase
              .from('users')
              .select('email, user_metadata')
              .eq('id', comment.user_id)
              .maybeSingle(); // Use maybeSingle instead of single to handle missing users

            if (userError) {
              logger.error('Error fetching user data:', userError);
              return {
                ...comment,
                email: `Unknown (ID: ${comment.user_id.substring(0, 8)}...)`,
                user_metadata: { first_name: 'Unknown', last_name: 'User' }
              };
            }

            // If user data exists, use it, otherwise provide a default
            if (userData) {
              return {
                ...comment,
                email: userData.email || `Unknown (ID: ${comment.user_id.substring(0, 8)}...)`,
                user_metadata: userData.user_metadata || { first_name: 'Unknown', last_name: 'User' }
              };
            } else {
              return {
                ...comment,
                email: `Unknown (ID: ${comment.user_id.substring(0, 8)}...)`,
                user_metadata: { first_name: 'Unknown', last_name: 'User' }
              };
            }
          } catch (err) {
            logger.error('Error processing comment user data:', err);
            return {
              ...comment,
              email: `Error (ID: ${comment.user_id.substring(0, 8)}...)`,
              user_metadata: { first_name: 'Error', last_name: 'Loading' }
            };
          }
        })
      );

      setComments(commentsWithUsers);
    } catch (err) {
      logger.error('Error fetching comments:', err);
    }
  }, [id, connectionError]);

  // Fetch comments only once, no real-time subscription
  useEffect(() => {
    fetchComments();

    const subscription = supabase
      .channel(`market-comments-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_comments',
          filter: `market_id=eq.${id}`
        },
        (payload) => {
          logger.info('MarketDetail: Comment update received:', payload);
          fetchComments();
        }
      )
      .subscribe((status) => {
        logger.info(`MarketDetail: Comments subscription status: ${status}`);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchComments, id]);

  // Fetch watchlist status when market or user changes
  useEffect(() => {
    const fetchWatchlistStatus = async () => {
      if (!user || !id) return;
      
      try {
        const { data, error } = await supabase
          .from('watchlists')
          .select('id')
          .eq('user_id', user.id)
          .eq('market_id', id)
          .maybeSingle();
          
        if (!error) {
          const isInWatchlist = !!data;
          setIsWatchlisted(isInWatchlist);
        } else {
          logger.error('Error fetching watchlist status:', error);
        }
      } catch (err) {
        logger.error('Exception fetching watchlist status:', err);
      }
    };
    
    fetchWatchlistStatus();
  }, [user, id]);

  // Check if user is market creator
  useEffect(() => {
    if (user && market) {
      setIsMarketCreator(user.id === market.created_by);
    }
  }, [user, market]);

  // Show edit/resolve tabs for private market creators (for active 'active', 'ended', 'expired', 'resolved', 'cancelled markets)
  const showCreatorControls = market?.is_private && isMarketCreator && (
    ['active', 'ended', 'endeds', 'expired', 'resolved', 'cancelled'].includes(market.status)
  );
  
  // Determine if editing should be allowed (separate from visibility)
  const canEditMarket = showCreatorControls && market?.status !== 'cancelled' && market?.status !== 'resolved';

  const handleBack = () => {
    navigate(isAdmin ? '/admin/dashboard' : '/markets');
  };

  const handlePostComment = async () => {
    if (!newComment.trim() || !user || !id) return;

    setCommentLoading(true);
    try {
      const { data: newCommentData, error } = await supabase
        .from('market_comments')
        .insert({
          market_id: id,
          user_id: user.id,
          content: newComment.trim()
        })
        .select('*')
        .single();

      if (error) throw error;
      
      // Clear the input field
      setNewComment('');
      
      // Immediately add the new comment to the UI
      if (newCommentData) {
        // Add user data to the new comment
        const commentWithUser = {
          ...newCommentData,
          email: user.email || '',
          user_metadata: user.user_metadata || {}
        };
        
        // Update comments state with the new comment
        setComments(prevComments => [commentWithUser, ...prevComments]);
      } else {
        // If we couldn't get the new comment data, manually fetch all comments
        await fetchComments();
      }
    } catch (err) {
      logger.error('Error posting comment:', err);
      setError('Failed to post comment');
    } finally {
      setCommentLoading(false);
    }
  };

  const formatDate = (date: string, short: boolean = false) => {
    return formatDateWithTimezone(date, short);
  };

  // Handle toggling the watchlist status
  const handleToggleWatchlist = async () => {
    if (!user || !id) {
      // Redirect to login if user is not authenticated
      navigate('/login', { state: { from: `/markets/${id}` } });
      return;
    }
    
    setIsUpdatingWatchlist(true);
    
    try {
      const { error } = await supabase.rpc('toggle_watchlist', {
        p_market_id: id
      });
      
      if (error) {
        logger.error('Error toggling watchlist:', error);
      } else {
        // Toggle local state
        setIsWatchlisted(!isWatchlisted);
      }
    } catch (error) {
      logger.error('Exception when toggling watchlist:', error);
    } finally {
      setIsUpdatingWatchlist(false);
    }
  };

  // Function to handle outcome selection and scroll to PlaceBetForm on mobile.
  // Auth gate: redirect to login if no session — the bet panel only opens for authenticated users.
  const handleSelectOutcome = (optionId: number) => {
    logger.info('[MarketDetail] handleSelectOutcome called with optionId:', optionId);
    logger.info('[MarketDetail] Market type:', market?.is_dynamic ? 'Dynamic' : 'Fixed');
    
    if (!user) {
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    if (selectedOptionId === optionId) {
      scrollToPlaceBetForm();
      return;
    }
    
    setSelectedOptionId(optionId);
    requestAnimationFrame(() => {
      scrollToPlaceBetForm();
    });
  };

  const updateLmsrTradeMode = useCallback(
    (mode: 'buy' | 'sell') => {
      setLmsrTradeMode(mode);
      const next = new URLSearchParams(searchParams);
      if (mode === 'sell') next.set('trade', 'sell');
      else {
        next.delete('trade');
        next.delete('option');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Modify pool update handler to update timestamp
  const updatePoolData = useCallback((optionId: number, newAmount: number) => {
    logger.info(`MarketDetail: Updating pool for option ${optionId} to ${newAmount}`);
    
    // Update the specific pool in the market
    setMarket(prevMarket => {
      if (!prevMarket || !prevMarket.marketPools) return prevMarket;
      
      // Create a new array of pools with the updated amount
      const updatedPools = prevMarket.marketPools.map(pool => {
        if (pool.market_option_id === optionId) {
          return { ...pool, total_amount: newAmount };
        }
        return pool;
      });
      
      // Calculate new total pool value for market state only
      const newTotalPool = updatedPools.reduce((sum, pool) => sum + pool.total_amount, 0);
      logger.info(`MarketDetail: New total pool value: ${newTotalPool} (updating market.totalPool only)`);
      
      // Don't update totalPoolValue directly - this should come from markets table
      // setTotalPoolValue(newTotalPool);
      // setTotalPoolUpdated(true);
      
      setLastUpdateTime(new Date().toLocaleTimeString());
      
      return {
        ...prevMarket,
        marketPools: updatedPools,
        // Update totalPool calculation
        totalPool: newTotalPool
      };
    });
  }, []);

  // Add comprehensive real-time subscriptions
  useEffect(() => {
    if (!id || connectionError) return;
    
    // Keep track of all subscriptions for cleanup
    const subscriptions: RealtimeChannel[] = [];
    
    // Define interface for market_pools table realtime changes
    interface MarketPoolRealtimePayload {
      market_option_id?: number; // Made optional to handle cases where it might be missing
      amount_after_fee?: number;
      // Add other relevant fields from your market_pools table
    }
    
    // Subscribe to market pool updates
    const marketPoolSubscription = supabase
      .channel(`market-pools-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_pools',
          filter: `market_id=eq.${id}`
        },
        (payload) => {
          logger.info('MarketDetail: Market pool update received:', payload);
          
          if (payload.new && typeof payload.new === 'object') {
            const newPoolData = payload.new as MarketPoolRealtimePayload;
            logger.info(`MarketDetail: Pool data changed. Old: ${JSON.stringify(payload.old)}, New: ${JSON.stringify(newPoolData)}`);
            
            // Update market pools directly
            setMarket(prevMarket => {
              if (!prevMarket) return null;
              
              const updatedPools = prevMarket.marketPools?.map(pool => {
                // Ensure newPoolData.market_option_id is defined before comparison
                if (newPoolData.market_option_id !== undefined && pool.market_option_id === newPoolData.market_option_id) {
                  const newAmount = pool.total_amount + (newPoolData.amount_after_fee || 0);
                  logger.info(`MarketDetail: Updating pool for option ${pool.market_option_id} to ${newAmount} from new bet`);
                  return {
                    ...pool,
                    total_amount: newAmount
                  };
                }
                return pool;
              });
              
              // Calculate new total pool value for market state only
              const newTotalPool = updatedPools?.reduce((sum, pool) => sum + pool.total_amount, 0) || 0;
              logger.info(`MarketDetail: New total pool value from bet: ${newTotalPool} (updating market.totalPool only)`);
              
              // No longer updating totalPoolValue directly - this will come from markets table
              // setTotalPoolValue(newTotalPool);
              // setTotalPoolUpdated(true);
              
              // Update last update time
              setLastUpdateTime(new Date().toLocaleTimeString());
              
              return {
                ...prevMarket,
                marketPools: updatedPools,
                volume: (prevMarket.volume || 0) + (newPoolData.amount_after_fee || 0),
                totalPool: newTotalPool,
                ...(prevMarket.engine_type === 'lmsr'
                  ? {}
                  : { total_bets: (prevMarket.total_bets || 0) + 1 }),
              };
            });

            if (marketEngineTypeRef.current === 'lmsr') {
              queueMicrotask(() => {
                void fetchMarket();
              });
            }

            // Note: We don't manually update unique_traders here anymore
            // The value will be updated via the marketStatusSubscription
            // when the database triggers update the markets table
            
            // Check if this bet was placed by the current user
            // Assuming user_id might come with the pool update if it's directly from a bet insert
            const betPayload = payload.new as { user_id?: string };
            if (user && betPayload.user_id === user.id) {
              setBetHistoryRefresh(prev => prev + 1);
            }
            
            // We no longer refresh market data after pool updates
            // This prevents unwanted page refreshes after placing a bet
            logger.info('MarketDetail: Pool updated, but skipping market refresh');
          }
        }
      )
      .subscribe((status) => {
        logger.info(`MarketDetail: Market pool subscription status: ${status}`);
        if (status === 'CLOSED') {
          logger.info(`Market pool subscription closed for market ${id}`);
        }
      });
    
    subscriptions.push(marketPoolSubscription);
    
    // Subscribe to market options updates
    const marketOptionsSubscription = supabase
      .channel(`market-options-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_options',
          filter: `market_id=eq.${id}`
        },
        (payload) => {
          logger.info('MarketDetail: Market option update received:', payload);
          
          if (payload.new && typeof payload.new === 'object' && payload.new !== null) {
            // Use type assertion to handle the payload.new object
            const newData = payload.new as { id?: number };
            
            if ('id' in newData && typeof newData.id === 'number') {
              logger.info('MarketDetail: Option data changed. Option ID:', newData.id);
              
              // Update the specific option in the market
              setMarket(prevMarket => {
                if (!prevMarket || !Array.isArray(prevMarket.market_options)) return prevMarket;
                
                // Create a new array of options with the updated data
                const updatedOptions = prevMarket.market_options.map(option => {
                  if (option.id === newData.id) {
                    return { ...option, ...newData };
                  }
                  return option;
                });
                
                return {
                  ...prevMarket,
                  market_options: updatedOptions
                };
              });
            } else {
              // Avoid auto-refresh if id is missing/invalid
              logger.info('MarketDetail: Option data missing valid id, but skipping refresh');
            }
          } else {
            // Avoid auto-refresh if payload structure is unexpected
            logger.info('MarketDetail: Unexpected payload structure, but skipping refresh');
          }
        }
      )
      .subscribe((status) => {
        logger.info(`MarketDetail: Market option subscription status: ${status}`);
        if (status === 'CLOSED') {
          logger.info(`Market option subscription closed for market ${id}`);
        }
      });
    
    subscriptions.push(marketOptionsSubscription);
    
    // Subscribe to market status changes
    const marketStatusSubscription = supabase
      .channel(`market-updates-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Change from 'UPDATE' to '*' to catch all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'markets',
          filter: `id=eq.${id}`
        },
        (payload) => {
          logger.info('Realtime market update received:', payload);
          logger.info('Update type:', payload.eventType);
          
          setLastUpdateTime(new Date().toLocaleTimeString());
          const updatedMarket = payload.new as MarketDataFromDb;

          // Debug unique_traders value specifically
          if (updatedMarket && 'unique_traders' in updatedMarket) {
            logger.info('💥 UNIQUE TRADERS UPDATE:', updatedMarket.unique_traders);
            logger.info('Current state unique_traders:', marketActivity.uniqueTraders);
          }

          // Debug active_bets_count value specifically
          if (updatedMarket && 'active_bets_count' in updatedMarket) {
            logger.info('💥 ACTIVE BETS COUNT UPDATE:', updatedMarket.active_bets_count);
          }

          // Debug total_pool value specifically
          if (updatedMarket && 'total_pool' in updatedMarket) {
            logger.info('💥 TOTAL POOL UPDATE:', updatedMarket.total_pool);
          }

          // Update states with strict type checks and defaults
          if (updatedMarket.unique_traders !== undefined) {
            logger.info(`Updating unique traders from ${marketActivity.uniqueTraders} to ${updatedMarket.unique_traders}`);
            setMarketActivity(prev => ({ ...prev, uniqueTraders: updatedMarket.unique_traders ?? prev.uniqueTraders }));
            
            // Also update the market state
            setMarket(prev => prev ? { ...prev, unique_traders: updatedMarket.unique_traders } : null);
          }
          if (updatedMarket.active_bets_count !== undefined && market) {
            logger.info(`Updating active bets count from ${market.active_bets_count || market.total_bets} to ${updatedMarket.active_bets_count}`);
            setMarket(prev => prev ? { ...prev, active_bets_count: updatedMarket.active_bets_count } : null);
          }
          if (updatedMarket.total_bets !== undefined && market) {
            setMarket(prev => prev ? { ...prev, total_bets: updatedMarket.total_bets ?? prev.total_bets } : null);
          }
          if (updatedMarket.lmsr_open_positions_count !== undefined && market) {
            setMarket(prev =>
              prev
                ? {
                    ...prev,
                    lmsr_open_positions_count:
                      updatedMarket.lmsr_open_positions_count ?? prev.lmsr_open_positions_count,
                  }
                : null,
            );
          }
          if (updatedMarket.total_pool !== undefined && market) {
            const dbTotalPoolRaw = updatedMarket.total_pool;
            const dbTotalPoolParsed = typeof dbTotalPoolRaw === 'string' ? parseFloat(dbTotalPoolRaw) : dbTotalPoolRaw;
            const dbTotalPool = typeof dbTotalPoolParsed === 'number' && !isNaN(dbTotalPoolParsed) ? dbTotalPoolParsed : market.totalPool || 0;
            
            logger.info(`Updating total pool from ${market.totalPool || 0} to ${dbTotalPool} (from markets table)`);
            
            // Update both the market.totalPool and the dedicated totalPoolValue state
            setMarket(prev => prev ? { ...prev, totalPool: dbTotalPool } : null);
            setTotalPoolValue(dbTotalPool);
            setTotalPoolUpdated(true);
            
            // Reset animation after 2 seconds
            setTimeout(() => setTotalPoolUpdated(false), 2000);
          }
          if (updatedMarket.volume !== undefined && market) {
            const volumeRaw = updatedMarket.volume;
            const volumeParsed = typeof volumeRaw === 'string' ? parseFloat(volumeRaw) : volumeRaw;
            const volume = typeof volumeParsed === 'number' && !isNaN(volumeParsed) ? volumeParsed : (market.volume ?? 0);
            setMarket(prev => prev ? { ...prev, volume: volume } : null);
          }
          if (updatedMarket.price_change_24h !== undefined) {
            const priceChangeRaw = updatedMarket.price_change_24h;
            const priceChangeParsed = typeof priceChangeRaw === 'string' ? parseFloat(priceChangeRaw) : priceChangeRaw;
            const priceChange = typeof priceChangeParsed === 'number' && !isNaN(priceChangeParsed) ? priceChangeParsed : 0;
            setMarketActivity(prev => ({ ...prev, priceChange24h: priceChange ?? prev.priceChange24h }));
          }
        }
      )
      .subscribe((status, err) => {
        logger.info(`Realtime subscription status for market ${id}:`, status);
        if (status === 'SUBSCRIBED') {
          setRealtimeStatus('Connected');
          logger.info(`Successfully subscribed to market ${id}`);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          setRealtimeStatus(`Error: ${status}`);
          logger.error(`Realtime subscription error for market ${id}:`, err || status);
          // Optional: Implement retry logic here
        } else if (status === 'CLOSED') {
          // This is expected during normal component unmounting, so don't treat as error
          logger.info(`Realtime subscription closed for market ${id}`);
          setRealtimeStatus('Closed');
        } else {
          setRealtimeStatus(status);
        }
      });
    
    subscriptions.push(marketStatusSubscription);
    
    // Subscribe to user bets to track new traders and volume changes
    const userBetsSubscription = supabase
      .channel(`user-bets-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_bets',
          filter: `market_id=eq.${id}`
        },
        (payload) => {
          logger.info('MarketDetail: New trade placed on this market:', payload);
          
          interface UserBetPayload {
            market_option_id: number;
            amount_after_fee: number;
            user_id: string;
            // Consider making this more specific if possible, otherwise 'any' might be needed
            // for truly dynamic or unknown additional properties from Supabase.
            [key: string]: unknown;
          }
          
          // Type check and assertion
          if (
            payload.new && 
            typeof payload.new === 'object' && 
            'market_option_id' in payload.new && 
            'amount_after_fee' in payload.new &&
            'user_id' in payload.new
          ) {
            const newBet = payload.new as UserBetPayload;
            
            // Update market pools directly
            setMarket(prevMarket => {
              if (!prevMarket) return null;
              
              const updatedPools = prevMarket.marketPools?.map(pool => {
                if (pool.market_option_id === newBet.market_option_id) {
                  const newAmount = pool.total_amount + (newBet.amount_after_fee || 0);
                  logger.info(`MarketDetail: Updating pool for option ${pool.market_option_id} to ${newAmount} from new bet`);
                  return {
                    ...pool,
                    total_amount: newAmount
                  };
                }
                return pool;
              });
              
              // Calculate new total pool value for market state only
              const newTotalPool = updatedPools?.reduce((sum, pool) => sum + pool.total_amount, 0) || 0;
              logger.info(`MarketDetail: New total pool value from bet: ${newTotalPool} (updating market.totalPool only)`);
              
              // No longer updating totalPoolValue directly - this will come from markets table
              // setTotalPoolValue(newTotalPool);
              // setTotalPoolUpdated(true);
              
              // Update last update time
              setLastUpdateTime(new Date().toLocaleTimeString());
              
              return {
                ...prevMarket,
                marketPools: updatedPools,
                volume: (prevMarket.volume || 0) + (newBet.amount_after_fee || 0),
                totalPool: newTotalPool,
                ...(prevMarket.engine_type === 'lmsr'
                  ? {}
                  : { total_bets: (prevMarket.total_bets || 0) + 1 }),
              };
            });

            if (marketEngineTypeRef.current === 'lmsr') {
              queueMicrotask(() => {
                void fetchMarket();
              });
            }

            // Note: We don't manually update unique_traders here anymore
            // The value will be updated via the marketStatusSubscription
            // when the database triggers update the markets table
            
            // Check if this bet was placed by the current user
            if (user && newBet.user_id === user.id) {
              setBetHistoryRefresh(prev => prev + 1);
              // No refresh needed - local state already updated
            } else {
              // For bets placed by other users, just update the local state without refresh
              logger.info('MarketDetail: Trade placed by another user, local state updated');
            }
          }
        }
      )
      .subscribe((status) => {
        logger.info(`MarketDetail: User bets subscription status: ${status}`);
        if (status === 'CLOSED') {
          logger.info(`User bets subscription closed for market ${id}`);
        }
      });
    
    subscriptions.push(userBetsSubscription);
    
    // Return cleanup function to unsubscribe from all channels
    return () => {
      subscriptions.forEach(subscription => {
        logger.info(`Unsubscribing from channel: ${subscription.topic}`);
        subscription.unsubscribe();
      });
    };
  }, [id, connectionError, fetchMarket, updatePoolData, user, marketActivity]);

  // Update the useEffect that handles totalPoolValue updates
  useEffect(() => {
    if (market && market.marketPools) {
      const newTotal = market.marketPools.reduce((sum, pool) => sum + pool.total_amount, 0);
      
      // Only update the market.totalPool property, not totalPoolValue
      // The totalPoolValue will come exclusively from the markets table now
      if (market.totalPool !== newTotal) {
        logger.info(`MarketDetail: Market pools changed, updating market.totalPool from ${market.totalPool} to ${newTotal} (not affecting totalPoolValue)`);
        setMarket(prev => prev ? { ...prev, totalPool: newTotal } : null);
      }
    }
  }, [market, market?.marketPools]);

  if (connectionError) {
    return (
      <div className="py-8">
        <div className="text-center text-red-600">
          Unable to connect to the server. Please check your internet connection and try again.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
        </div>
      </div>
    );
  }

  if (error || !market) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-600">{error || 'Failed to load market details. Please try again later.'}</div>
        </div>
      </div>
    );
  }

  // Generate SEO description from market data
  const seoDescription = market.description 
    ? market.description.slice(0, 155) + (market.description.length > 155 ? '...' : '')
    : `Trade on "${market.title}" - ${market.category} prediction market on Wayja.`;

  // Generate structured data for this market
  const marketStructuredData = getMarketStructuredData({
    title: market.title,
    description: seoDescription,
    category: market.category,
    url: `/markets/${market.id}`,
    image: market.image_url || market.featured_image_url,
    dateCreated: market.created_at,
    dateModified: market.updated_at,
    status: market.status,
    options: market.market_options?.map(opt => ({
      name: opt.name,
      probability: opt.probability || 0
    }))
  });

  // Breadcrumb structured data
  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: '/' },
    { name: 'Markets', url: '/markets' },
    { name: market.category, url: `/markets?category=${market.category}` },
    { name: market.title, url: `/markets/${market.id}` }
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={market.title}
        description={seoDescription}
        image={`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/og-image?type=prediction-card&market_id=${market.id}`}
        url={`/markets/${market.id}`}
        type="article"
        structuredData={[marketStructuredData, breadcrumbData]}
      />
      {/* Development-only debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-2 right-2 bg-gray-800 text-white text-xs p-2 rounded opacity-75 z-50">
          <div>Realtime: {realtimeStatus}</div>
          <div>Channels: {supabase.getChannels().length}</div>
          <div>Market Size: {formatCurrency(totalPoolValue)}</div>
          <div>Last Update: {lastUpdateTime}</div>
          <button 
            onClick={() => {
              // Force a full refresh
              fetchMarket();
              setLastUpdateTime(new Date().toLocaleTimeString());
              toast.success('Market data refreshed', { duration: 2000 });
            }}
            className="mt-1 text-xs bg-gray-700 hover:bg-gray-600 py-1 px-2 rounded"
          >
            Force Refresh
          </button>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-wayja-teal"></div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-600 bg-red-50 p-4 rounded-lg shadow-sm max-w-md mx-auto text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <h3 className="font-bold text-lg mb-2">Error Loading Market</h3>
            <p>{error}</p>
            <button 
              onClick={handleBack}
              className="mt-4 px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      ) : market ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Top Navigation */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <button
              onClick={handleBack}
              className="flex items-center text-sm md:text-base text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 mr-2 shrink-0" />
              <span>Back to Markets</span>
            </button>
            
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleToggleWatchlist}
                disabled={isUpdatingWatchlist}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <Star 
                  className={`w-4 h-4 md:w-5 md:h-5 ${isWatchlisted ? 'fill-wayja-teal text-wayja-teal' : 'text-gray-400'}`} 
                />
              </button>
              {market.status === 'active' && (
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] md:text-xs font-medium bg-green-100 text-green-800">
                  <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 shrink-0"></span>
                  Live Market
                </div>
              )}
              {market.status === 'resolved' && (
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Award className="w-3 h-3 mr-1" />
                  Resolved
                </div>
              )}
              {market.status === 'cancelled' && (
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Cancelled
                </div>
              )}
              {market.status === 'pending_approval' && (
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <Clock className="w-3 h-3 mr-1" />
                  Pending Approval
                </div>
              )}
              {market.status === 'rejected' && (
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  <XCircle className="w-3 h-3 mr-1" />
                  Rejected
                </div>
              )}
              
              <div className="hidden sm:inline-flex flex-col items-end gap-0.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-800 max-w-[min(100%,14rem)]">
                <span className="text-[10px] uppercase tracking-wide text-gray-500 leading-none">Market resolution</span>
                <span className="inline-flex items-center gap-1 min-w-0">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{resolutionCountdown}</span>
                </span>
              </div>
              
              <button 
                className="text-gray-500 hover:text-gray-700 transition-colors p-1.5 rounded-full hover:bg-gray-100"
                title="Share market"
                onClick={async () => {
                  const shareUrl = window.location.href;
                  const shareText = `I predict on "${market.title}" — join me on Wayja!`;
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: market.title,
                        text: shareText,
                        url: shareUrl,
                      });
                    } catch {
                      // User cancelled or share failed — fall back to clipboard
                      await navigator.clipboard.writeText(shareUrl);
                      toast.success('Link copied to clipboard!', { duration: 2000 });
                    }
                  } else {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success('Link copied to clipboard!', { duration: 2000 });
                  }
                }}
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Expandable Rejection Alert */}
          {market.status === 'rejected' && market.rejection_reason && (
            <div className="mb-6 bg-red-50 border border-red-300 rounded-md shadow-sm overflow-hidden">
              <button
                type="button"
                onClick={() => setIsRejectionExpanded(!isRejectionExpanded)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-red-100 focus:outline-none"
              >
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium text-red-700">Market Rejected - Click to see details</span>
                </div>
                {isRejectionExpanded ? (
                  <ChevronUp className="h-5 w-5 text-red-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-red-500" />
                )}
              </button>
              {/* Collapsible Content */}
              {isRejectionExpanded && (
                <div className="p-3 border-t border-red-200 bg-white">
                  <div className="text-xs text-red-700 space-y-1">
                    <p><strong>Reason:</strong> {market.rejection_reason}</p>
                    <p><strong>Date:</strong> {new Date(market.updated_at || Date.now()).toLocaleDateString()}</p>
                    <p className="mt-2">If you have questions or wish to address the issues, please contact support.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expandable Cancellation Alert */}
          {market.status === 'cancelled' && market.cancellation_reason && (
            <div className="mb-6 bg-red-50 border border-red-300 rounded-md shadow-sm overflow-hidden">
              <button
                type="button"
                onClick={() => setIsCancellationExpanded(!isCancellationExpanded)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-red-100 focus:outline-none"
              >
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                  <span className="text-sm font-medium text-red-700">Market Cancelled - Click to see details</span>
                </div>
                {isCancellationExpanded ? (
                  <ChevronUp className="h-5 w-5 text-red-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-red-500" />
                )}
              </button>
              {/* Collapsible Content */}
              {isCancellationExpanded && (
                <div className="p-3 border-t border-red-200 bg-white">
                  <div className="text-xs text-red-700 space-y-1">
                    <p><strong>Reason:</strong> {market.cancellation_reason}</p>
                    <p><strong>Date:</strong> {new Date(market.updated_at || Date.now()).toLocaleDateString()}</p>
                    <p className="mt-2">If you have questions about this cancellation, please contact support.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Main Content Layout */}
          <div className="flex flex-col lg:flex-row gap-6 mb-6">
            {/* Left side - Main Content */}
            <div className="lg:flex-grow lg:w-2/3">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Title & Category */}
                <div className="p-6 pb-4">
                  <div className="mb-2">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {market.category}
                      </div>
                      {/* Short Code for Private Markets */}
                      {market.is_private && market.short_code && (
                        <div className="flex items-center gap-2 group relative">
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-wayja-light-teal/25 rounded-md">
                            <Lock className="w-4 h-4 text-wayja-teal" />
                            <span className="font-mono text-sm font-semibold text-wayja-dark-teal">
                              {market.short_code}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(market.short_code!);
                                toast.success('Code copied to clipboard!');
                              }}
                              className="p-1 text-wayja-teal hover:text-wayja-dark-teal transition-colors"
                              title="Copy code"
                            >
                              <Clipboard className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="text-sm text-gray-500">Share code to invite others</span>
                          
                          {/* Tooltip */}
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-full left-0 mt-2 w-64 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg z-50">
                            <p>This is a private market. Share this code with others to give them access. They can enter this code by clicking "Private Market" in the dashboard.</p>
                            <div className="absolute -top-1 left-6 transform -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-xl md:text-3xl font-bold text-gray-900">{market.title}</h1>
                      </div>
                      <button
                        onClick={() => {
                          logger.info('Manual refresh triggered');
                          fetchMarket();
                          toast.success('Market data refreshed', { duration: 2000 });
                        }}
                        className="ml-2 p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                        title="Refresh market data"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                          <path d="M3 3v5h5"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Market Stats Grid - Compact */}
                <div className="bg-gray-50 border-y border-gray-200 p-3">
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-x-2 gap-y-3">
                    {/* Total Market Size */}
                    <div className="">
                      <div className="text-gray-500 text-xs mb-0.5 flex items-center">
                        <Trophy className="w-3 h-3 mr-1 text-gray-400" />
                        <span>Market Size</span>
                      </div>
                      <div className="flex items-center">
                        <div className={`text-base font-semibold transition-all duration-500 ${
                          totalPoolUpdated ? 'text-wayja-teal scale-110' : 'text-gray-900'
                        }`}>
                          {formatCurrency(totalPoolValue)}
                        </div>
                        <button 
                          onClick={() => {
                            if (market && market.marketPools) {
                              const newTotal = market.marketPools.reduce((sum, pool) => sum + pool.total_amount, 0);
                              logger.info(`Manual update of total pool: ${newTotal} (updating market.totalPool only)`);
                              // Only update market.totalPool, not totalPoolValue directly
                              setMarket(prev => prev ? { ...prev, totalPool: newTotal } : null);
                              // Don't update totalPoolValue directly
                              // setTotalPoolValue(newTotal);
                              // setTotalPoolUpdated(true);
                              setLastUpdateTime(new Date().toLocaleTimeString());
                              // setTimeout(() => setTotalPoolUpdated(false), 2000);
                            }
                          }}
                          className="ml-1 text-gray-400 hover:text-gray-600"
                          title="Refresh market size"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" 
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                            <path d="M3 3v5h5"></path>
                          </svg>
                        </button>
                      </div>
                    </div>


                    {/* Unique Traders - Admin Only */}
                    {isAdmin && (
                      <div className="">
                        <div className="text-gray-500 text-xs mb-0.5 flex items-center">
                          <Users className="w-3 h-3 mr-1 text-gray-400" />
                          <span>Traders</span>
                        </div>
                        <div className="text-base font-semibold text-gray-900">
                          {market.unique_traders ?? marketActivity.uniqueTraders ?? 0}
                        </div>
                      </div>
                    )}
                    {/* Open positions: LMSR = active user_bet rows with shares; pool ≈ active stakes */}
                    <div className="">
                      <div className="text-gray-500 text-xs mb-0.5 flex items-center">
                        <BarChart className="w-3 h-3 mr-1 text-gray-400" />
                        <span>Open positions</span>

                      </div>
                      <div className="text-base font-semibold text-gray-900">
                        {market.engine_type === 'lmsr'
                          ? (market.lmsr_open_positions_count ?? 0)
                          : (market.active_bets_count || market.total_bets || 0)}
                      </div>
                    </div>

                    {/* 24h Change */}
                    <div className="">
                      <div className="text-gray-500 text-xs mb-0.5 flex items-center">
                        <Percent className="w-3 h-3 mr-1 text-gray-400" />
                        <span>24h Chg</span>
                      </div>
                      <div className={`text-base font-semibold ${
                        typeof marketActivity.priceChange24h === 'number' && marketActivity.priceChange24h > 0 ? 'text-green-600' : 
                        typeof marketActivity.priceChange24h === 'number' && marketActivity.priceChange24h < 0 ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {typeof marketActivity.priceChange24h === 'number' 
                          ? `${marketActivity.priceChange24h > 0 ? '+' : ''}${marketActivity.priceChange24h.toFixed(1)}%`
                          : 'N/A'
                        }
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Add the graph section after the market stats grid */}
                <div className="bg-white border-y border-gray-200 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {chartMode === 'price' ? 'Price History' : 'Trade volume'}
                      </h3>
                      <div className="flex rounded-full overflow-hidden bg-gray-100 border border-gray-200/80">
                        <button
                          type="button"
                          onClick={() => setChartMode('price')}
                          className={`px-3 py-1 text-sm whitespace-nowrap ${
                            chartMode === 'price'
                              ? 'bg-wayja-teal text-white font-medium'
                              : 'text-gray-600 hover:bg-gray-200/70'
                          }`}
                        >
                          Price
                        </button>
                        <button
                          type="button"
                          onClick={() => setChartMode('volume')}
                          className={`px-3 py-1 text-sm whitespace-nowrap ${
                            chartMode === 'volume'
                              ? 'bg-wayja-teal text-white font-medium'
                              : 'text-gray-600 hover:bg-gray-200/70'
                          }`}
                        >
                          Volume
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedTimeRange('day')}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedTimeRange === 'day'
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        24H
                      </button>
                      <button
                        onClick={() => setSelectedTimeRange('week')}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedTimeRange === 'week'
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        7D
                      </button>
                      <button
                        onClick={() => setSelectedTimeRange('month')}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedTimeRange === 'month'
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        30D
                      </button>
                      <button
                        onClick={() => setSelectedTimeRange('all')}
                        className={`px-3 py-1 rounded-full text-sm ${
                          selectedTimeRange === 'all'
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                    </div>
                  </div>
                  <MarketOptionsGraph
                    market={market}
                    data={chartMode === 'price' ? optionHistory : volumeHistory}
                    timeRange={selectedTimeRange}
                    mode={chartMode}
                    formatZar={(n) => formatCurrency(n, 2)}
                    avgBuyByOptionPercent={avgBuyByOptionPercent}
                  />
                </div>
                
                {/* Market Tabs */}
                <div className="border-b border-gray-200">
                  <div className="flex overflow-x-auto">
                    <button
                      onClick={() => setActiveTab('outcomes')}
                      className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors ${
                        activeTab === 'outcomes'
                          ? 'text-wayja-teal border-b-2 border-wayja-teal'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      Outcomes
                    </button>
                    
                    {user && (
                      <button
                        onClick={() => setActiveTab('bets')}
                        className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors ${
                          activeTab === 'bets'
                            ? 'text-wayja-teal border-b-2 border-wayja-teal'
                            : 'text-gray-500 hover:text-gray-900'
                        }`}
                      >
                        My Trades
                      </button>
                    )}
                    
                    {/* Add Edit tab for private market creators */}
                    {showCreatorControls && (
                      <button
                        onClick={() => setActiveTab('edit')}
                        className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors flex items-center ${
                          activeTab === 'edit'
                            ? 'text-wayja-teal border-b-2 border-wayja-teal'
                            : 'text-gray-500 hover:text-gray-900'
                        } ${!canEditMarket ? 'opacity-70' : ''}`}
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit Market
                        {!canEditMarket && market?.status === 'cancelled' && (
                          <span className="absolute top-2 right-2 flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                          </span>
                        )}
                      </button>
                    )}
                    
                    {/* Add Resolve tab for private market creators */}
                    {showCreatorControls && (
                      <button
                        onClick={() => setActiveTab('resolve')}
                        className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors flex items-center ${
                          activeTab === 'resolve'
                            ? 'text-wayja-teal border-b-2 border-wayja-teal'
                            : 'text-gray-500 hover:text-gray-900'
                        }`}
                      >
                        <Award className="w-4 h-4 mr-2" />
                        Resolve
                      </button>
                    )}
                    
                    {/* Add Cancel tab for private market creators */}
                    {showCreatorControls && (
                      <button
                        onClick={() => setActiveTab('cancel')}
                        className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors flex items-center ${
                          activeTab === 'cancel'
                            ? 'text-wayja-teal border-b-2 border-wayja-teal' // Highlight if active
                            : 'text-gray-500 hover:text-gray-900'
                        }`}
                      >
                        <XCircle className="w-4 h-4 mr-2" /> 
                        Cancel Market
                      </button>
                    )}
                    
                    <button
                      onClick={() => setActiveTab('comments')}
                      className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors flex items-center ${
                        activeTab === 'comments'
                          ? 'text-wayja-teal border-b-2 border-wayja-teal'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <span>Discussion</span>
                      {comments.length > 0 && (
                        <span className="ml-2 bg-gray-100 text-gray-600 rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          {comments.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('description')}
                      className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors ${
                        activeTab === 'description'
                          ? 'text-wayja-teal border-b-2 border-wayja-teal'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      About Market
                    </button>
                    
                    <button
                      onClick={() => setActiveTab('rules')}
                      className={`relative py-3 px-4 md:py-4 md:px-6 font-medium text-sm md:text-base whitespace-nowrap transition-colors ${
                        activeTab === 'rules'
                          ? 'text-wayja-teal border-b-2 border-wayja-teal'
                          : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      Rules
                    </button>
                    
                  </div>
                </div>
                
                {/* Tab Content - Directly Under Tabs */}
                <div className="p-6">
                  {activeTab === 'outcomes' && (
                    <div className="space-y-6">
                      {/* Debug info */}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="p-3 bg-gray-100 rounded-md text-sm">
                          <strong>DEBUG:</strong> Market type: {market.market_type}, 
                          Options count: {market.market_options?.length || 0}
                        </div>
                      )}
                      
                      {/* Outcome options */}
                      <div className="space-y-3">
                        {Array.isArray(market.market_options) ? (
                          market.market_options
                            .map((option) => {
                              try {
                                // *** Adapt structure for calculatePricing (assuming it needs MarketDataFromDb) ***
                                const pricingMarketData: MarketDataFromDb = {
                                  id: market.id,
                                  title: market.title,
                                  is_private: market.is_private,
                                  category: market.category,
                                  description: market.description,
                                  created_by: market.created_by,
                                  status: market.status,
                                  total_bets: market.total_bets,
                                  market_options: market.market_options,
                                  market_pools: market.marketPools,
                                  is_dynamic: market.is_dynamic,
                                  engine_type: market.engine_type,
                                  lmsr_b: market.lmsr_b,
                                };
                                // Pass option.id (number) to calculatePricing
                                const pricing = marketEngine.calculatePricing(pricingMarketData, option.id);
                                return { option, pricing };
                              } catch (error) {
                                logger.error(`Error calculating pricing for option ${option.id}:`, error);
                                return { option, pricing: null }; // Handle pricing errors
                              }
                            })
                            // Sort by probability (descending) using calculated pricing
                            .sort((a, b) => (b.pricing?.yesPercentage ?? 0) - (a.pricing?.yesPercentage ?? 0))
                            // Map to JSX
                            .map(({ option, pricing }) => {
                              if (!pricing) { // Skip rendering if pricing failed
                                return (
                                   <div key={option.id} className="p-4 rounded-lg border-2 border-red-200 bg-red-50">
                                     <p className="text-red-700">Error calculating pricing for: {option.name}</p>
                                   </div>
                                );
                              }
                              
                              // *** Determine if option is tradeable (detailed check) ***
                              let isTradeable = false;
                              const now = Date.now();
                              const marketResolutionTime = market.resolution_time ? new Date(market.resolution_time).getTime() : Infinity;

                              if (market.status === 'active' && option.status === 'active') {
                                isTradeable = now < marketResolutionTime;
                              }
                              // *** End tradeability check ***

                              // Check if this option is the winning option
                              const isWinningOption = market.status === 'resolved' && market.winning_option_id === option.id;

                              return (
                                <div 
                                  key={option.id} 
                                  onClick={() => isTradeable ? handleSelectOutcome(option.id) : null}
                                  className={`p-3 md:p-4 rounded-lg border-2 transition-all group ${
                                    isWinningOption
                                    ? 'border-green-500 bg-green-50 shadow-md' // Winning option highlight
                                    : !isTradeable
                                      ? option.status === 'eliminated'
                                        ? 'border-red-100 bg-red-50 opacity-60 cursor-not-allowed' // Eliminated option
                                        : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed' // Ended option
                                      : selectedOptionId === option.id
                                        ? 'border-wayja-teal bg-wayja-teal/5 shadow-sm cursor-pointer' // Selected
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer' // Default hoverable
                                  }`}
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                      {/* Add trophy icon and special styling for winning option */}
                                      <div className="flex items-center gap-2">
                                        {isWinningOption && (
                                          <span className="text-green-500" title="Winning Option">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M6 3.75A2.75 2.75 0 018.75 1h2.5A2.75 2.75 0 0114 3.75v.443c.572.055 1.14.122 1.706.2C17.053 4.582 18 5.75 18 7.07v3.469c0 1.126-.694 2.191-1.83 2.54-1.952.599-4.024.921-6.17.921s-4.219-.322-6.17-.921C2.694 12.73 2 11.665 2 10.539V7.07c0-1.321.947-2.489 2.294-2.676A41.047 41.047 0 016 4.193V3.75zm6.5 0v.325a41.622 41.622 0 00-5 0V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25zM10 10a1 1 0 00-1 1v.01a1 1 0 001 1h.01a1 1 0 001-1V11a1 1 0 00-1-1H10z" clipRule="evenodd" />
                                              <path d="M3 15.055v-.684c.126.053.255.1.39.142 2.092.642 4.313.987 6.61.987 2.297 0 4.518-.345 6.61-.987.135-.041.264-.089.39-.142v.684c0 1.347-.985 2.53-2.363 2.686a41.454 41.454 0 01-9.274 0C3.985 17.585 3 16.402 3 15.055z" />
                                            </svg>
                                          </span>
                                        )}
                                        {/* Add eliminated indicator */}
                                        {option.status === 'eliminated' && (
                                          <span className="text-red-500" title="Eliminated Option">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                          </span>
                                        )}
                                        <h4 className={`font-medium text-sm md:text-base flex items-center gap-2 min-w-0 ${
                                          isWinningOption 
                                            ? 'text-green-700' 
                                            : option.status === 'eliminated'
                                              ? 'text-red-700 line-through'
                                              : 'text-gray-900'
                                        } mb-1 ${isTradeable ? 'group-hover:text-wayja-teal' : ''} transition-colors`}>
                                          {option.image_url ? (
                                            <img
                                              src={option.image_url}
                                              alt=""
                                              className="w-6 h-6 rounded-full object-cover shrink-0"
                                            />
                                          ) : null}
                                          <span className="min-w-0">{option.name}</span>
                                          {isWinningOption && <span className="ml-2 text-xs md:text-sm text-green-600 font-normal">(Winner)</span>}
                                          {option.status === 'eliminated' && <span className="ml-2 text-xs md:text-sm text-red-600 font-normal">(Eliminated)</span>}
                                        </h4>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                        <div className="flex items-center space-x-1 text-xs md:text-sm text-gray-500">
                                          {market.engine_type === 'lmsr' ? (
                                            <span>{pricing.yesPercentage.toFixed(1)}% chance</span>
                                          ) : (
                                            <span>{pricing.yesPercentage.toFixed(1)}% Probability</span>
                                          )}
                                        </div>
                                        {market.engine_type === 'lmsr' ? (
                                          <>
                                            <span className="hidden sm:inline text-gray-300">•</span>
                                            <div className="relative flex items-center flex-wrap gap-x-1 text-xs md:text-sm text-gray-500">
                                              {(pricing.yesPercentage === 0 || pricing.odds === Number.MAX_SAFE_INTEGER) ? (
                                                <span>—</span>
                                              ) : (
                                                <>
                                                  <span className={`font-semibold ${isWinningOption ? 'text-green-600' : isTradeable ? 'text-wayja-teal' : 'text-gray-600'}`}>
                                                    {pricing.odds.toFixed(2)}x
                                                  </span>
                                                  <span className="text-gray-400">(est. return figure)</span>
                                                </>
                                              )}
                                              {isTradeable && pricing.yesPercentage > 0 && pricing.odds !== Number.MAX_SAFE_INTEGER && (
                                                <div className="hidden group-hover:block absolute bottom-full left-0 mb-2 w-56 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                                                  Estimated return: {pricing.odds.toFixed(2)}x. Actual payout depends on final market state at resolution.
                                                  <div className="absolute bottom-0 left-6 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                                                </div>
                                              )}
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <span className="hidden sm:inline text-gray-300">•</span>
                                            <div className="flex items-center space-x-1 text-xs md:text-sm text-gray-500">
                                              <span>Market Size: {formatCurrency(market.marketPools?.find(pool => pool.market_option_id === option.id)?.total_amount ?? 0)}</span>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center space-x-4 pt-2 sm:pt-0">
                                      {market.engine_type === 'lmsr' ? (
                                        <div className={`text-center rounded-lg py-1.5 px-2.5 md:px-3 transition-colors ${isWinningOption ? 'bg-green-100' : isTradeable ? 'bg-wayja-teal/10 group-hover:bg-wayja-teal/20' : 'bg-gray-100'}`}>
                                          <div className="text-[11px] md:text-xs text-gray-500 mb-0.5">Buy</div>
                                          <div className={`text-xs md:text-sm font-bold leading-tight ${isWinningOption ? 'text-green-600' : isTradeable ? 'text-wayja-teal' : 'text-gray-400'}`}>
                                            {pricing.yesPercentage === 0 ? (
                                              <span>—</span>
                                            ) : (
                                              <span>R{formatSharePriceZar(pricing.yesPercentage / 100)}/share</span>
                                            )}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className={`text-center rounded-lg py-1.5 px-2.5 md:px-3 relative transition-colors ${isWinningOption ? 'bg-green-100' : isTradeable ? 'bg-wayja-teal/10 group-hover:bg-wayja-teal/20' : 'bg-gray-100'}`}>
                                          <div className="text-[11px] md:text-xs text-gray-500 mb-0.5">Return</div>
                                          <div className={`text-xs md:text-sm font-bold leading-tight ${isWinningOption ? 'text-green-600' : isTradeable ? 'text-wayja-teal' : 'text-gray-400'}`}>
                                            {(pricing.yesPercentage === 0 || pricing.odds === Number.MAX_SAFE_INTEGER)
                                              ? (
                                                <span>—</span>
                                              )
                                              : (
                                                <span>{pricing.odds.toFixed(2)}x</span>
                                              )
                                            }
                                          </div>
                                          {isTradeable && pricing.yesPercentage > 0 && pricing.odds !== Number.MAX_SAFE_INTEGER && (
                                            <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-56 p-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                                              Current return: {pricing.odds.toFixed(2)}x. Actual payout depends on final market size at resolution.
                                              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Button logic - Update button text for winning option */}
                                      <button 
                                        className={`flex items-center space-x-1 px-2.5 py-1 md:px-3 md:py-1.5 text-xs md:text-sm rounded border transition-colors ${ 
                                          isWinningOption
                                          ? 'bg-green-500 text-white border-green-500 cursor-default' // Winning option button
                                          : !isTradeable 
                                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' // Not tradeable
                                            : selectedOptionId === option.id
                                              ? 'bg-wayja-teal text-white border-wayja-teal' // Selected
                                              : 'bg-white text-wayja-teal border-wayja-teal hover:bg-wayja-teal/10' // Default Selectable
                                        }`}
                                        onClick={(e) => {
                                          if (!isTradeable) return; 
                                          e.stopPropagation();
                                          if (user) {
                                            handleSelectOutcome(option.id);
                                          } else {
                                            navigate('/login', { state: { from: location.pathname } });
                                          }
                                        }}
                                        disabled={!isTradeable || isWinningOption} 
                                      >
                                        {/* Updated Button Text */}
                                        {isWinningOption ? (
                                          <span>Winner</span>
                                        ) : selectedOptionId === option.id ? (
                                          <span>Selected</span>
                                        ) : isTradeable ? (
                                          <span>{(userSharesByOption[option.id] ?? 0) > 0 ? 'Trade Again' : 'Buy'}</span>
                                        ) : (
                                          <span>Ended</span>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                  
                                  {/* Probability bar - update color for winning option */}
                                  <div className="mt-3 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full transition-all duration-500 ease-in-out ${
                                        isWinningOption ? 'bg-green-500' : isTradeable ? 'bg-wayja-teal' : 'bg-gray-300'
                                      }`}
                                      style={{ width: `${pricing.yesPercentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              );
                            })
                        ) : (
                          <div className="p-4 rounded-lg border-2 border-gray-200 text-center">
                            <p className="text-gray-500">No options available for this market</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'description' && (
                    <div>
                      {/* Cover Image - In description tab */}
                      {market.cover_image && (
                        <div className="w-full h-48 sm:h-56 overflow-hidden rounded-lg mb-6">
                          <img
                            src={market.cover_image}
                            alt={market.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Rejection reason display in description tab is now removed, handled by the top expandable alert */}
                      
                      <p className="text-gray-700 whitespace-pre-line mb-6">{market.description}</p>
                      
                      {/* Market insights */}
                      <div className="mt-6 bg-gray-50 rounded-lg p-4">
                        <h3 className="text-base font-medium text-gray-900 mb-3">Market Insights</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="text-sm text-gray-500 mb-1">Recent Market Size</div>
                            <div className="text-xl font-semibold text-gray-900" key={`recent-volume-${marketActivity.recentVolume}`}>
                              {formatCurrency(market.recent_volume || marketActivity.recentVolume)}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Trading volume in past 48 hours
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="text-sm text-gray-500 mb-1">Recent Traders</div>
                            <div className="text-xl font-semibold text-gray-900">
                              {market.unique_traders ?? marketActivity.uniqueTraders ?? 0}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Unique users who have placed bets
                            </div>
                          </div>
                          
                          <div className="bg-white rounded-lg p-3 border border-gray-200">
                            <div className="text-sm text-gray-500 mb-1">Service Fee</div>
                            <div className="text-xl font-semibold text-gray-900">
                              8%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'rules' && (
                    <div>
                      <div className="prose prose-sm max-w-none text-gray-700">
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <h4 className="font-medium text-gray-900 mb-2">Market Resolution</h4>
                          <p className="text-gray-700">
                            {market.resolution_time?.trim()
                              ? formatDate(market.resolution_time)
                              : 'To be announced'}
                          </p>
                        </div>
                        <div className="whitespace-pre-line">
                          {market.rules || 'No specific rules provided for this market.'}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'bets' && user && (
                    <div>
                      <GroupedBetHistory 
                        refreshTrigger={betHistoryRefresh} 
                        marketId={id || ''}
                        showTitle={false}
                      />
                    </div>
                  )}
                  
                  {activeTab === 'edit' && showCreatorControls && market && (
                    <div className="space-y-6">
                      {/* Use admin editor for admins, creator editor for regular users */}
                      {userIsAdmin ? (
                        <AdminMarketEditor 
                          market={market} 
                          marketId={id || ''}
                          onMarketUpdate={() => fetchMarket()}
                          canEdit={true}
                        />
                      ) : (
                        <CreatorMarketEditor 
                          market={market} 
                          marketId={id || ''}
                          onMarketUpdate={() => fetchMarket()}
                          canEdit={canEditMarket || false}
                        />
                      )}
                      {market.status === 'cancelled' && (
                        <div className="p-4 bg-amber-50 border border-amber-300 rounded-md text-amber-700 text-sm mt-4">
                          <p className="font-medium">This market has been cancelled and cannot be edited.</p>
                          {market.cancellation_reason && (
                            <p className="mt-1">Cancellation reason: {market.cancellation_reason}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {activeTab === 'resolve' && showCreatorControls && market && (
                    <div className="space-y-6">
                      <MarketResolver 
                        market={market} 
                        marketId={market.id}
                        onResolved={() => {
                          logger.info('Market resolved, refreshing data...');
                          fetchMarket(); // Re-fetch market data
                        }}
                      />
                    </div>
                  )}
                  
                  {activeTab === 'cancel' && showCreatorControls && market && (
                    <div className="space-y-6">
                      {/* Placeholder for MarketCanceller component */}
                      <MarketCanceller 
                        market={market} 
                        marketId={market.id} // market.id is a string, matching p_market_id TEXT
                        onCancelled={() => {
                          logger.info('MarketDetail: Market cancelled by creator, refreshing data...');
                          fetchMarket(); // Re-fetch market data to update status and cancellation reason
                          // toast.success('Market status updated.'); // Removed this line, MarketCanceller handles its own toast
                        }}
                      />
                    </div>
                  )}
                  
                  {activeTab === 'comments' && (
                    <div>
                      <div className="space-y-4">
                        {/* Comment form now appears at the top */}
                        {user && (
                          <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Share your thoughts</h4>
                            <div className="flex space-x-4">
                              <div className="flex-grow">
                                <textarea
                                  value={newComment}
                                  onChange={(e) => setNewComment(e.target.value)}
                                  placeholder="What do you think about this market?"
                                  className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-none focus:ring-2 focus:ring-wayja-teal/50 focus:border-wayja-teal"
                                  rows={3}
                                ></textarea>
                              </div>
                              <div>
                                <button
                                  onClick={() => {
                                    if (newComment.trim()) {
                                      handlePostComment();
                                    }
                                  }}
                                  disabled={commentLoading || !newComment.trim()}
                                  className="h-full px-4 bg-wayja-teal text-white rounded-lg disabled:opacity-50 hover:bg-opacity-90 transition-colors"
                                >
                                  {commentLoading ? 'Posting...' : 'Post'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {comments.length === 0 ? (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 mb-4">No comments yet. Be the first to share your thoughts!</p>
                            
                            {!user && (
                              <button
                                onClick={() => navigate('/login', { state: { from: location.pathname } })}
                                className="px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors"
                              >
                                Login to Comment
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {comments.map(comment => (
                              <div key={comment.id} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0">
                                    {comment.user_metadata?.avatar_url ? (
                                      <img 
                                        src={comment.user_metadata.avatar_url} 
                                        alt="Profile" 
                                        className="w-10 h-10 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-10 h-10 rounded-full bg-wayja-teal/20 flex items-center justify-center text-wayja-teal uppercase font-bold">
                                        {comment.user_metadata?.first_name?.[0] || comment.email[0]}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900">
                                      {comment.user_metadata?.first_name
                                        ? `${comment.user_metadata.first_name} ${comment.user_metadata.last_name || ''}`
                                        : comment.email.split('@')[0]}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(comment.created_at).toLocaleString()}
                                    </p>
                                    <div className="mt-2 text-sm text-gray-700 whitespace-pre-line">
                                      {comment.content}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right side - Betting Widget */}
            <div className="lg:w-1/3">
              {showSharePricedIntro && !showFirstTradeGate && (
                <SharePricedTradingIntroModal onClose={() => setShowSharePricedIntro(false)} />
              )}

              {showFirstTradeGate && (
                <FirstTradeGateModal
                  onClose={() => {
                    setShowFirstTradeGate(false);
                  }}
                />
              )}

              {user && userProfile && selectedOptionId !== null && (
                <div ref={placeBetFormRef} className="sticky top-24 relative z-30">
                  {market.engine_type === 'lmsr' && (
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-3 bg-white shadow-sm">
                      <button
                        type="button"
                        onClick={() => updateLmsrTradeMode('buy')}
                        className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                          lmsrTradeMode === 'buy'
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Buy
                      </button>
                      <button
                        type="button"
                        onClick={() => updateLmsrTradeMode('sell')}
                        className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                          lmsrTradeMode === 'sell'
                            ? 'bg-wayja-teal text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        Sell
                      </button>
                    </div>
                  )}
                  {/* Debug market data before passing to PlaceBetForm */}
                  <RenderDebug market={market} />
                  {/* Use logging outside JSX */}
                  {(() => {
                    logger.debug('MarketDetail passing market to PlaceBetForm:', {
                      id: market.id,
                      is_private: market.is_private,
                      fixed_fee: market.fixed_fee,
                      fixed_fee_type: typeof market.fixed_fee
                    });
                    return null;
                  })()}
                  
                  <PlaceBetForm
                    market={market}
                    userProfile={userProfile}
                    selectedOptionId={selectedOptionId}
                    lmsrTradeMode={lmsrTradeMode}
                    onCancel={() => setSelectedOptionId(null)}
                    onBetSuccess={async () => {
                      setBetHistoryRefresh((prev) => prev + 1);
                      await fetchMarket();
                      // First-Trade Gate: check if this was the user's first bet
                      // and profile is not yet phone-verified
                      const stage = userProfile.profile_completion_stage ?? 'signup_complete';
                      if (stage === 'signup_complete') {
                        const { count } = await supabase
                          .from('user_bets')
                          .select('id', { count: 'exact', head: true })
                          .eq('user_id', user.id);
                        if ((count ?? 0) === 1) {
                          setShowSharePricedIntro(false);
                          dismissSharePricedIntroPersist();
                          setShowFirstTradeGate(true);
                        }
                      }
                    }}
                  />
                </div>
              )}
              
              {user && userProfile && selectedOptionId === null && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 sticky top-24">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Ready to Place a Trade?</h3>
                    
                    <div className="rounded-lg bg-gray-50 p-4 mb-4">
                      <div className="flex items-start">
                        <div className="mr-3 mt-0.5 text-gray-400">
                          <AlertCircle className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            Select an outcome from the available options to place your trade.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* User wallet status */}
                    <div className="mt-2 pt-4 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-600">Your Balance</div>
                        <div className="font-medium">{formatCurrency(userProfile.wallet_balance)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {!user && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 sticky top-24">
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Want to place a trade?</h3>
                    
                    <div className="rounded-lg bg-wayja-teal/5 p-4 mb-4">
                      <div className="flex items-start">
                        <div className="mr-3 mt-0.5 text-wayja-teal">
                          <Percent className="w-5 h-5" />
                        </div>
                        <div>
                          {/* Check if market_options exists before mapping */}
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            {/* Use market_options */} 
                            Potential returns up to {market.market_options && market.market_options.length > 0 ? Math.max(...market.market_options.map(o => o.odds ?? 0)).toFixed(2) : 'N/A'}x
                          </p>
                          <p className="text-sm text-gray-600">
                            Login now to place trades on this market.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <button
                        onClick={() => navigate('/login', { state: { from: location.pathname } })}
                        className="w-full px-4 py-3 bg-wayja-teal text-white rounded-lg hover:bg-opacity-90 transition-colors font-medium"
                      >
                        Login to Place Trade
                      </button>
                      
                      <button
                        onClick={() => navigate('/register', { state: { from: location.pathname } })}
                        className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Sign Up
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Similar Markets */}
          {/* <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">Similar Markets</h3>
            </div>
            <div className="p-4">
              <div className="text-center text-sm text-gray-500 py-2">
                <p>Similar markets would be displayed here.</p>
                <p>Coming soon!</p>
              </div>
            </div>
          </div> */}
        </div>
      ) : null}
    </div>
  );
}